import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { OperationalCharge, OperationalChargeStats } from '../../models/forwarding/operational-charge.model';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class OperationalChargesService {
  private apiUrl = `${environment.apiUrl}/forwarding/operational-charges`;
  private charges$ = new BehaviorSubject<OperationalCharge[]>([]);

  constructor(private http: HttpClient) {
    this.loadCharges();
  }

  private loadCharges(): void {
    this.http.get<OperationalCharge[]>(this.apiUrl).subscribe({
      next: (charges) => {
        // Convert date strings to Date objects
        const processedCharges = charges.map(c => ({
          ...c,
          createdAt: new Date(c.createdAt),
          updatedAt: c.updatedAt ? new Date(c.updatedAt) : undefined
        }));
        this.charges$.next(processedCharges);
      },
      error: (error) => {
        console.error('Error loading operational charges:', error);
        // Fallback to mock data if API fails
        this.loadMockData();
      }
    });
  }

  getCharges(): Observable<OperationalCharge[]> {
    return this.charges$.asObservable();
  }

  getStats(): OperationalChargeStats {
    const charges = this.charges$.value;
    const mandatoryCount = charges.filter(c => !c.isOptional).length;
    const totalPrice = charges.reduce((sum, c) => sum + c.defaultPrice, 0);

    return {
      totalCharges: charges.length,
      mandatoryCharges: mandatoryCount,
      optionalCharges: charges.length - mandatoryCount,
      averagePrice: charges.length > 0 ? totalPrice / charges.length : 0
    };
  }

  addCharge(charge: Omit<OperationalCharge, 'id' | 'createdAt'>): void {
    this.http.post<OperationalCharge>(this.apiUrl, charge).subscribe({
      next: () => this.loadCharges(),
      error: (error) => console.error('Error adding charge:', error)
    });
  }

  updateCharge(id: number, updates: Partial<OperationalCharge>): void {
    this.http.put<OperationalCharge>(`${this.apiUrl}/${id}`, updates).subscribe({
      next: () => this.loadCharges(),
      error: (error) => console.error('Error updating charge:', error)
    });
  }

  deleteCharge(id: number): void {
    this.http.delete(`${this.apiUrl}/${id}`).subscribe({
      next: () => this.loadCharges(),
      error: (error) => console.error('Error deleting charge:', error)
    });
  }

  filterCharges(searchTerm: string, mode?: string, chargeType?: string): OperationalCharge[] {
    let filtered = this.charges$.value;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(c =>
        c.feeName.toLowerCase().includes(term) ||
        c.description?.toLowerCase().includes(term)
      );
    }

    if (mode && mode !== 'ALL_MODES') {
      filtered = filtered.filter(c => c.mode === mode || c.mode === 'ALL');
    }

    if (chargeType && chargeType !== 'ALL_TYPES') {
      filtered = filtered.filter(c => c.chargeType === chargeType);
    }

    return filtered;
  }

  private getNextId(): number {
    const charges = this.charges$.value;
    return charges.length > 0 ? Math.max(...charges.map(c => c.id)) + 1 : 1;
  }

  private loadMockData(): void {
    const mockCharges: OperationalCharge[] = [
      {
        id: 1,
        feeName: 'TOF Handling Manifest',
        mode: 'SEA_LCL',
        chargeCode: 'DC',
        chargeBasis: 'Per BL (Bill of Lading)',
        defaultPrice: 1800,
        currency: 'PHP',
        chargeType: 'Flat',
        isOptional: false,
        portApplication: 'Destination',
        description: 'BL Fee, Manifest/TOF/Handling',
        isAgentSpecific: false,
        isTaxable: false,
        isActive: true,
        createdAt: new Date('2025-01-01')
      },
      {
        id: 2,
        feeName: 'Trucking Fee (Per Trip)',
        mode: 'SEA_FCL',
        chargeCode: 'OC',
        chargeBasis: 'Per Trip',
        defaultPrice: 310,
        currency: 'USD',
        chargeType: 'Flat',
        isOptional: false,
        portApplication: 'Both',
        description: 'Trucking Fee, Rider Fee',
        isAgentSpecific: false,
        isTaxable: false,
        isActive: true,
        createdAt: new Date('2025-01-01')
      },
      {
        id: 3,
        feeName: 'Processing Second Container',
        mode: 'ALL',
        chargeCode: 'CB',
        chargeBasis: 'Fixed Amount',
        defaultPrice: 1000,
        currency: 'PHP',
        chargeType: 'Flat',
        isOptional: false,
        portApplication: 'Both',
        description: 'Brokerage Fee, Documentation Fee',
        isAgentSpecific: false,
        isTaxable: false,
        isActive: true,
        createdAt: new Date('2025-01-01')
      },
      {
        id: 4,
        feeName: 'Tabs booking',
        mode: 'ALL',
        chargeCode: 'PC',
        chargeBasis: 'Per Container',
        defaultPrice: 1100,
        currency: 'PHP',
        chargeType: 'Flat',
        isOptional: false,
        portApplication: 'Both',
        description: 'Ocean Freight (FCL), Carrier Charges',
        isAgentSpecific: false,
        isTaxable: false,
        isActive: true,
        createdAt: new Date('2025-01-01')
      }
    ];

    this.charges$.next(mockCharges);
  }
}